import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  ?.trim()
  .replace(/\s+/g, '')
  .replace(/%20/gi, '')
  .replace(/\/+$/, '')
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim()

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check your .env file.')
}

// Custom fetch wrapper for debugging upload issues (dev only)
const customFetch: typeof fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  const isStorageUpload = url.includes('/storage/') && init?.method === 'POST'

  if (import.meta.env.DEV && isStorageUpload) {
    console.log('[Supabase Fetch] Storage upload request:', url)
    console.log('[Supabase Fetch] Headers:', init?.headers)
    console.log('[Supabase Fetch] Body type:', init?.body?.constructor?.name)
  }

  try {
    const response = await fetch(input, init)
    if (import.meta.env.DEV && isStorageUpload) {
      console.log('[Supabase Fetch] Response status:', response.status)
    }
    return response
  } catch (error) {
    if (import.meta.env.DEV && isStorageUpload) {
      console.error('[Supabase Fetch] Upload failed:', error)
      console.error('[Supabase Fetch] Error name:', (error as Error).name)
      console.error('[Supabase Fetch] Error message:', (error as Error).message)
    }
    throw error
  }
}

// Using untyped client until database migration is applied
// After migration: run `npm run db:generate` to generate proper types
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    fetch: customFetch,
  },
})

// Auth helpers
export const auth = {
  signIn: async (email: string, password: string) => {
    return supabase.auth.signInWithPassword({ email, password })
  },

  signUp: async (email: string, password: string, metadata?: { full_name?: string }) => {
    return supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    })
  },

  signOut: async () => {
    return supabase.auth.signOut()
  },

  getSession: async () => {
    return supabase.auth.getSession()
  },

  getUser: async () => {
    return supabase.auth.getUser()
  },

  onAuthStateChange: (callback: Parameters<typeof supabase.auth.onAuthStateChange>[0]) => {
    return supabase.auth.onAuthStateChange(callback)
  },
}

/**
 * Convert a Supabase PostgrestError (or any non-Error object) into a proper Error instance.
 * This ensures Sentry captures a real stack trace instead of
 * "Object captured as exception with keys: code, details, hint, message".
 */
export function toError(error: unknown): Error {
  if (error instanceof Error) return error
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const pgError = error as { message: string; code?: string; details?: string; hint?: string }
    const err = new Error(pgError.message)
    err.name = 'PostgrestError'
    // Preserve Supabase error metadata for programmatic handling
    ;(err as any).code = pgError.code
    ;(err as any).details = pgError.details
    ;(err as any).hint = pgError.hint
    return err
  }
  return new Error(String(error))
}

// Storage helpers for document uploads
export const storage = {
  uploadDocument: async (bucket: string, path: string, file: File) => {
    return supabase.storage.from(bucket).upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    })
  },

  getPublicUrl: (bucket: string, path: string) => {
    return supabase.storage.from(bucket).getPublicUrl(path)
  },

  downloadDocument: async (bucket: string, path: string) => {
    return supabase.storage.from(bucket).download(path)
  },

  deleteDocument: async (bucket: string, paths: string[]) => {
    return supabase.storage.from(bucket).remove(paths)
  },
}
